import { captureMessage } from '@sentry/vue';
import { LocalStorageHelper } from '@/services/LocalStorageHelper';

/**
 * Utility class for working with stale promo records in local storage
 */
export default class StalePromotionRegistry {
  stalePromosKey = 'stalePromotions';

  // Keep these relatively compact since local storage may have arbitrary limits
  categoryKeyPrefix = 'cat';
  optionIdKeyPrefix = 'opt';
  stalePromoIdPrefixDelimiter = ':';
  stalePromoIdPartDelimiter = '-';
  stalePromotionData: Set<string>;

  constructor(private localStorageHelper: LocalStorageHelper) {
    const rawData = this.localStorageHelper.getItem(this.stalePromosKey);
    this.stalePromotionData = rawData ? new Set(JSON.parse(rawData)) : new Set<string>();
  }

  isOptionPromotionStale(category: string, optionId: number): boolean {
    return this.stalePromotionData.has(this.getKey(category, optionId));
  }

  setStalePromotions(staleOptionPromotions: OptionPromotions[]) {
    staleOptionPromotions.forEach((optionPromotions: OptionPromotions) => {
      optionPromotions.optionIds.forEach((optionId: number) => {
        this.stalePromotionData.add(this.getKey(optionPromotions.category, optionId));
      });
    });
    this.flushData();
  }

  cleanUpUnusedRecords(currentOptionPromotions: OptionPromotions[]) {
    // Tidy up local storage by removing orphaned records for options no longer promoted.
    // This also allows us to "reset" records for items if applied accidentally.
    const categoryOptionMap = new Map<string, number[]>();
    currentOptionPromotions.forEach((optionPromos) => {
      categoryOptionMap.set(optionPromos.category, optionPromos.optionIds);
    });

    const cleanPromotionData = new Set<string>();
    this.stalePromotionData.forEach((key: string) => {
      const parsedKey = this.parseKey(key);
      if (parsedKey) {
        const isCurrent = categoryOptionMap.get(parsedKey.category)?.includes(parsedKey.optionId);
        if (isCurrent) {
          cleanPromotionData.add(key);
        }
      }
    });
    this.stalePromotionData = cleanPromotionData;
    this.flushData();
  }

  private flushData() {
    const rawData = JSON.stringify([...this.stalePromotionData]);
    const success = this.localStorageHelper.setItem(this.stalePromosKey, rawData);
    if (!success) {
      captureMessage('Failed to store stale promotion records - error on setItem!');
    }
  }

  private getKey(category: string, optionId: number): string {
    const categoryKeyPart = `${this.categoryKeyPrefix}${this.stalePromoIdPrefixDelimiter}${category}`;
    const optionIdKeyPart = `${this.optionIdKeyPrefix}${this.stalePromoIdPrefixDelimiter}${optionId}`;
    return [categoryKeyPart, optionIdKeyPart].join(this.stalePromoIdPartDelimiter);
  }

  private parseKey(key: string): { category: string; optionId: number } | null {
    const keyParts = key.split(this.stalePromoIdPartDelimiter);
    const category = keyParts[0].split(this.stalePromoIdPrefixDelimiter)[1];
    const optionId = +keyParts[1].split(this.stalePromoIdPrefixDelimiter)[1];
    return category && optionId ? { category, optionId } : null;
  }
}
